/**
 * The greeting's word for a member with no usable name: "Hi there,". The one
 * place it is written; a sentence that ENDS on the name leaves the name out
 * instead ({@link commaFirstName}).
 */
export const GREETING_WITHOUT_NAME = "there";

/**
 * First words that are a title or a placeholder, never someone's name — and
 * "there", the rule's own answer for no name.
 */
const NOT_A_NAME: ReadonlySet<string> = new Set([
  "mr", "mrs", "ms", "miss", "mx", "dr", "prof", "sir", "madam",
  "member", "user", "guest", "customer", "client", "unknown", "test", "testing",
  "whatsapp", "instagram", "facebook", "lead", "new", "none", "null", "na", "n/a",
  GREETING_WITHOUT_NAME,
]);

/** Characters a paste can leave in a name that are never part of one. */
const INVISIBLE = /[\u200B\u2060\uFEFF]/gu;

/**
 * A word that reads as a name: letters, each with any accent marks, joined by a
 * zero-width (non-)joiner where a script needs one, or by an inner apostrophe
 * (`'` or `’`) or hyphen.
 */
const NAME_WORD = /^\p{L}\p{M}*(?:\p{L}\p{M}*|[\u200C\u200D](?=\p{L})|['’-](?=\p{L}))*$/u;

/**
 * The greeting name for a member: the first word of `display_name` when it
 * reads as a name, else "there" ("Hi there,"). One rule for every email that
 * greets a member by name — the flow engine's `{{ member.first_name }}` token
 * (in the WhatsApp drip steps too), the HTML each email's renderer greets with,
 * the booking-change notices, the transactional emails canonical actions render
 * themselves (2026-10-07, `email_payment_link`) and the email newsletter —
 * and, since 2026-10-23 (first-name-followups; Mario: "Same rule for
 * WhatsApp"), every WhatsApp message the code composes for a member: the transactional
 * templates, the payment confirmation, the booking follow-up, the feedback
 * ask, the WhatsApp newsletter, the reminder thanks, the account-link line
 * and a poll's hold, which read the first word with helpers of their own
 * until then. It lives here, in the leaf package all of them share, so an
 * action greeting a member does not reach into the flow engine to do it.
 *
 * Strict since 2026-10-22 (plaintext-first-name), when the newsletter's
 * stricter twin was folded into it: a one-to-one email greets people whose
 * names were typed by themselves into a public form, imported, or left as a
 * phone number or an email address, exactly as a bulk send does — and
 * "Hi +447700900123," is worse than "Hi there," whoever it goes to.
 *
 *   - the first word, split on any whitespace (a no-break space or a tab too),
 *     after NFC normalisation — a name typed with separate accent marks (a
 *     macOS or PDF paste) reads as the same letters — and with the invisible
 *     characters a paste leaves behind removed;
 *   - letters (with their accent marks) only, with an inner apostrophe or `-`
 *     (`O'Neil`, `Anne-Marie`), 2 to 24 long — no digits, `@`, dots or emoji;
 *   - not a title or a placeholder word ({@link NOT_A_NAME});
 *   - all-lower or ALL-CAPS is put in name case (`anna` / `ANNA` → `Anna`,
 *     `anne-marie` → `Anne-Marie`); a mixed-case name is kept as typed
 *     (`McKenzie`).
 *
 * "Patrick Vogl" → "Patrick"; null, blank or not a name → "there". Applying it
 * twice changes nothing, so a caller that has already put "there" in place of
 * a missing name (the flow walkers' `member.display_name`) still gets "there".
 */
export function memberFirstName(displayName: string | null): string {
  return memberKnownFirstName(displayName) ?? GREETING_WITHOUT_NAME;
}

/**
 * The same rule, saying `null` where {@link memberFirstName} says "there" — for
 * a message that greets a member with no name in words of its own rather than
 * with "there" (first-name-followups, 2026-10-23).
 */
export function memberKnownFirstName(displayName: string | null): string | null {
  if (displayName === null) return null;
  const first =
    displayName.normalize("NFC").replace(INVISIBLE, "").trim().split(/\s+/u)[0] ?? "";
  if (!NAME_WORD.test(first)) return null;
  const length = [...first].length;
  if (length < 2 || length > 24) return null;
  if (NOT_A_NAME.has(first.toLowerCase())) return null;
  const lower = first.toLowerCase();
  const upper = first.toUpperCase();
  if (first !== lower && first !== upper) return first;
  return lower.replace(/(^|['’-])(\p{L})/gu, (_m, sep: string, ch: string) => sep + ch.toUpperCase());
}

/**
 * The name where a sentence ENDS on it: ", Anna" — or nothing at all for a
 * member with no name, so "Reserved for you, Anna." reads "Reserved for you."
 * and "How did it go, Anna?" reads "How did it go?", never "…, there". Mario,
 * 22 Oct 2026: "Drop the name in '…, there' headings" (first-name-followups).
 * "Hi there," keeps the word: a greeting reads with it, a sentence's close
 * does not.
 *
 * Takes the greeting name already worked out — {@link memberFirstName}'s
 * answer, which every email's renderer and the flow engine's
 * `{{ member.first_name }}` are given — and leaves anything else as it is
 * given, without the spaces around it: a renderer prints what its caller
 * passes, and the editor's starting HTML passes placeholders.
 */
export function commaFirstName(firstName: string): string {
  const name = firstName.trim();
  return name.length === 0 || name.toLowerCase() === GREETING_WITHOUT_NAME ? "" : `, ${name}`;
}

/**
 * How a template staff write can name a member against the one rule above
 * (saved-edit-name-rules, 2026-10-23):
 *
 *   - `display_name_printed` — `{{ member.display_name }}`, the name as it is
 *     stored: the full name as typed into a form or imported, or a phone
 *     number or an email address left in its place — "Hi Charlotte Brown," at
 *     best, "Hi +447700900123," at worst. Greet with `{{ member.first_name }}`.
 *   - `first_name_after_comma` — a sentence that ends on
 *     ", {{ member.first_name }}", which reads "…, there" for everyone with no
 *     name on file. Write `{{ member.comma_first_name }}` (", Anna", or
 *     nothing; {@link commaFirstName}).
 *   - `member_address_after_comma` — the same, on the booking-change emails'
 *     greeting name `{{ trigger.member_address }}` (the same name, or
 *     "there"): write `{{ trigger.comma_member_address }}`.
 *
 * One reading for every surface that takes such a template: a saved email
 * edit (`edit_email_template`, and the send path, which does not use a stored
 * one that breaks it), a `/flows` live edit (`checkFlowLiveEdit`) and the
 * shipped flow files' and designs' own tests. The tokens are read as the flow
 * interpolator reads them, which fills both: whitespace — a line break or a
 * tab too — inside the braces. Between the comma and the name, markup an
 * email renders as nothing or a space (a tag, a no-break space) does not hide
 * the comma.
 */
export type MemberNameTemplateProblem =
  | "display_name_printed"
  | "first_name_after_comma"
  | "member_address_after_comma";

const PRINTS_DISPLAY_NAME = /\{\{\s*member\.display_name\s*\}\}/u;

/** What may sit between a comma and the name and still read ", <name>" in an email. */
const COMMA_THEN = String.raw`,(?:\s|&nbsp;|&#160;|&#[xX]0*[aA]0;|<[^<>]*>)*`;

/**
 * The greeting names a sentence must not end on after a comma, each with
 * the token that ends one on it instead — in the order they are reported.
 */
const COMMA_RULES: ReadonlyArray<{
  readonly problem: Exclude<MemberNameTemplateProblem, "display_name_printed">;
  readonly token: string;
  readonly commaToken: string;
  readonly pattern: RegExp;
}> = [
  {
    problem: "first_name_after_comma",
    token: "member.first_name",
    commaToken: "member.comma_first_name",
    pattern: new RegExp(String.raw`${COMMA_THEN}\{\{\s*member\.first_name\s*\}\}`, "u"),
  },
  {
    problem: "member_address_after_comma",
    token: "trigger.member_address",
    commaToken: "trigger.comma_member_address",
    pattern: new RegExp(String.raw`${COMMA_THEN}\{\{\s*trigger\.member_address\s*\}\}`, "u"),
  },
];

/** Every string in a template value, however deep, as the interpolator walks it. */
function templateStrings(value: unknown): ReadonlyArray<string> {
  if (typeof value === "string") return [value];
  if (Array.isArray(value)) return value.flatMap(templateStrings);
  if (value !== null && typeof value === "object") return Object.values(value).flatMap(templateStrings);
  return [];
}

/**
 * The ways `template` — a string, or a step's `input_template` field however
 * deep — names the member against the rule, each once, the stored name first;
 * empty = none.
 */
export function memberNameTemplateProblems(template: unknown): ReadonlyArray<MemberNameTemplateProblem> {
  const strings = templateStrings(template);
  const problems: MemberNameTemplateProblem[] = [];
  if (strings.some((s) => PRINTS_DISPLAY_NAME.test(s))) problems.push("display_name_printed");
  for (const rule of COMMA_RULES) {
    if (strings.some((s) => rule.pattern.test(s))) problems.push(rule.problem);
  }
  return problems;
}

/**
 * One problem in words, for the part of a template it was found in ("the
 * HTML", "step 2: 'body'"): what it does to the member, and what to write
 * instead.
 */
export function memberNameTemplateProblemWords(part: string, problem: MemberNameTemplateProblem): string {
  const comma = COMMA_RULES.find((rule) => rule.problem === problem);
  return comma === undefined
    ? `${part} prints {{ member.display_name }}, the member's name as it is stored — a full name, or a phone number or an email address: greet with {{ member.first_name }}, which says "there" when there is no name`
    : `${part} ends a sentence on ", {{ ${comma.token} }}", which reads "…, there" for a member with no name: use {{ ${comma.commaToken} }} instead (", Anna", or nothing)`;
}

/**
 * {@link memberNameTemplateProblems} for an email's HTML and plain text, one
 * sentence per problem saying which part and what to write instead — the
 * words `edit_email_template` refuses a save with and the dashboard's View
 * shows for a stored edit that is not sent. Empty = both parts name the
 * member by the rule.
 */
export function emailTemplateNameProblems(args: {
  readonly html: string;
  readonly text: string | null;
}): ReadonlyArray<string> {
  const parts: ReadonlyArray<readonly [string, string | null]> = [
    ["the HTML", args.html],
    ["the plain-text version", args.text],
  ];
  return parts.flatMap(([part, value]) =>
    value === null
      ? []
      : memberNameTemplateProblems(value).map((problem) => memberNameTemplateProblemWords(part, problem)),
  );
}
